import React, { useState } from "react";
import { Alert, Button, Stack } from "@mui/material";
import { getErrorMessage } from "utils/errorHelpers";
import { ActionDialog } from "components/ActionDialog/ActionDialog";
import { AlertSeverity, useAlerts } from "components/AlertProvider/AlertProvider";
import { orderToasts } from "areas/orders/components/chrome/orderToasts";
import { retryOrderBilling } from "areas/orders/services/orderService";

/**
 * Re-enqueues billing setup for an accepted order. Stub-period only: once the fulfilment aggregate
 * lands, evaluation republishes the hand-over on its own and this action is deleted.
 * @param root0
 * @param root0.odNumber
 */
export function RetryBillingAction({ odNumber }: { odNumber: string }) {
  const { sendNewAlert } = useAlerts();
  const [show, setShow] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  const confirm = async () => {
    setError(undefined);
    try {
      await retryOrderBilling(odNumber);
      setShow(false);
      sendNewAlert(orderToasts.billingRetryQueued, AlertSeverity.Success);
    } catch (retryError) {
      setError(retryError);
    }
  };

  return (
    <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
      <Button
        variant="outlined"
        size="small"
        onClick={() => {
          setError(undefined);
          setShow(true);
        }}
      >
        Retry billing
      </Button>
      <ActionDialog
        title="Retry billing setup"
        description={
          <>
            This re-runs billing setup for this order. Safe to repeat: it searches the billing system before creating
            anything.
            {!!error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {getErrorMessage(error)}
              </Alert>
            )}
          </>
        }
        show={show}
        confirmText="Retry"
        onConfirm={confirm}
        onCancel={() => setShow(false)}
      />
    </Stack>
  );
}
